import { useEffect, useState } from 'react';

import Head from 'next/head';
import { useScrollbarWidth } from 'react-use';

import { useFocusMethod } from '~/features/accessibility/hooks/useFocusMethod';
import { CheckoutLayout } from '~/features/checkout/components/CheckoutLayout/CheckoutLayout';
import N20MainMenu, { NAVIGATION_MAIN_DEFAULT_SECONDARY_ID } from '~/features/navigation/components/N20MainMenu/N20MainMenu';
import { N60Footer } from '~/features/navigation/components/N60Footer/N60Footer';
import { ILink, INavigationNode, IPages, IVerticalMenu } from '~/lib/data-contract';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';
import { useNavigation } from '~/shared/utils/navigation/hooks/useNavigation';
import { PageContent } from '~/templates/blocks/components/PageContent/PageContent';

import { PageLoadingIndicator } from './PageLoadingIndicator';

type Props = {
	children?: React.ReactNode;
	title?: string;
	isCheckout: boolean;
	page: IPages;
};

const createCommerceVerticalMenuPoint = (menuNodes: INavigationNode[] = [], link: ILink): IVerticalMenu => {
	return {
		id: NAVIGATION_MAIN_DEFAULT_SECONDARY_ID,
		link: link,
		subMenu: menuNodes,
		type: 'verticalMenu',
	};
};

export const Layout = ({ children, title = 'Rosendahl.com', isCheckout = false, page }: Props): JSX.Element => {
	const [live, setLive] = useState(false);
	const scrollbarWidth = useScrollbarWidth();

	const {
		data: { header: { home } = {}, navigation: { metaMenu, mainMenu } = {}, vertical },
	} = useFrame();
	const {
		data: { verticals = [] },
	} = useNavigation();

	// Enable focus method tracking.
	useFocusMethod(true);

	useEffect(() => {
		setLive(true);
	}, []);

	const isCommerceSite = vertical && !vertical.brandNo;
	const hasMetaHeader = !isCommerceSite && !!metaMenu?.items?.length;

	const menuNodes: IVerticalMenu[] = isCommerceSite ? [createCommerceVerticalMenuPoint(mainMenu, home || verticals[0].link), ...verticals] : verticals;


	return (
		<>
			<Head>
				<title>{title}</title>
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="initial-scale=1.0, width=device-width"
				/>
			</Head>
			<style dangerouslySetInnerHTML={{ __html: `html {--scrollbar-width: ${live ? scrollbarWidth : 0}px}` }} />
			<div className={`theme--${vertical?.brandNo || 'default'}`}>
				{isCheckout ? (
					<CheckoutLayout>{children}</CheckoutLayout>
				) : (
					<>
						<PageLoadingIndicator />
						<N20MainMenu
							menuNodes={menuNodes}
							hasMetaHeader={hasMetaHeader}
							currentVertical={vertical}
							metaMenu={metaMenu}
						/>
						<PageContent page={page}>{children}</PageContent>
						<N60Footer></N60Footer>
					</>
				)}
			</div>
		</>
	);
};
